"use client";

import { usePathname } from "next/navigation";

/**
 * Custom hook to get the active path in a Next.js application
 * @returns An object containing:
 * - pathname: The current pathname
 * - isActive: A function to check if a given path is active
 * - isActiveExact: A function to check if a given path exactly matches the current path
 */
export function useActivePath() {
  const pathname = usePathname();

  /**
   * Check if the given path is active (either exact match or starts with the path)
   * @param path The path to check
   * @param exact Whether to check for an exact match
   * @returns boolean indicating if the path is active
   */
  const isActive = (path: string): boolean => {
    // Handle home page special case
    if (path === "/") {
      return pathname === "/";
    }

    // For other paths, check if the current path starts with the given path
    return pathname.startsWith(path);
  };

  /**
   * Check if the given path exactly matches the current path
   * @param path The path to check
   * @returns boolean indicating if the path exactly matches
   */
  const isActiveExact = (path: string): boolean => {
    return pathname === path;
  };

  return {
    pathname,
    isActive,
    isActiveExact,
  };
}

export default useActivePath;
